import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Dialog,
  EmptyState,
  IconClients,
  IconCopy,
  IconPlus,
  IconSearch,
  Input,
  Skeleton,
  useToast,
} from "@dream/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { ClientMark } from "../components/client-mark.tsx";
import {
  ClientEditorDialog,
  publicClientUrl,
} from "../components/clients/client-editor-dialog.tsx";
import { useAdminCatalog } from "../hooks/use-calendar.ts";
import { type CatalogClient, inviteEmailSummary } from "../lib/client-emails.ts";
import { calendarSearch } from "../router.tsx";

/** Cadastro consolidado: ficha do cliente, e-mails da equipe e o que vai na agenda. */

function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function ClientsPage() {
  const catalog = useAdminCatalog();
  const toast = useToast();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogClient | null>(null);
  const [search, setSearch] = useState("");

  function openCreate(): void {
    setEditing(null);
    setEditorOpen(true);
  }

  function openEdit(client: CatalogClient): void {
    setEditing(client);
    setEditorOpen(true);
  }

  async function copyClientUrl(handle: string): Promise<void> {
    const url = publicClientUrl(handle);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado", url);
    } catch {
      toast.error("Não foi possível copiar", url);
    }
  }

  const clients = catalog.clients as CatalogClient[];
  const needle = fold(search.trim());
  const visibleClients =
    needle === ""
      ? clients
      : clients.filter((client) => {
          const emails = (client.emails ?? []).map((item) => item.email).join(" ");
          return (
            fold(client.name).includes(needle) ||
            client.handle.includes(needle) ||
            (client.email ?? "").toLowerCase().includes(needle) ||
            emails.toLowerCase().includes(needle)
          );
        });

  return (
    <div className="dream-scroll min-h-0 flex-1 overflow-auto p-4 md:p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[1.25rem] font-bold leading-[1.35] tracking-[0] text-[var(--color-ink)] sm:text-[1.5rem]">
              Clientes
            </h1>
            <p className="mt-1 max-w-2xl text-[0.875rem] leading-5 text-[var(--color-ink-secondary)]">
              Uma ficha por cliente: identidade, senha do portal, e-mails da equipe para o convite
              da Agenda e o que aparece na agenda pública. Clique no cartão para editar.
            </p>
          </div>
          <Button variant="primary" className="w-full shrink-0 sm:w-auto" onClick={openCreate}>
            <IconPlus className="size-4" />
            Novo cliente
          </Button>
        </div>

        {clients.length > 0 ? (
          <label className="relative mb-4 block max-w-md" htmlFor="client-search">
            <span className="sr-only">Buscar cliente</span>
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-ink-muted)]" />
            <Input
              className="pl-9"
              id="client-search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, @handle ou e-mail"
              type="search"
              value={search}
            />
          </label>
        ) : null}

        {catalog.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        ) : clients.length === 0 ? (
          <Card>
            <EmptyState
              icon={<IconClients className="size-5" />}
              title="Nenhum cliente cadastrado"
              description="Nome, senha de 4 dígitos, e-mails da equipe e o endereço /@cliente. O cliente abre a agenda por esse link."
              action={
                <Button variant="primary" onClick={openCreate}>
                  <IconPlus className="size-4" />
                  Cadastrar cliente
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visibleClients.length === 0 ? (
              <Card className="sm:col-span-2 lg:col-span-3">
                <EmptyState
                  icon={<IconSearch className="size-5" />}
                  title="Nenhum cliente com esse termo"
                  description="Tente o nome da marca, o @handle ou um e-mail da equipe."
                />
              </Card>
            ) : null}
            {visibleClients.map((client) => {
              const clientProjects = catalog.projects.filter((p) => p.clientId === client.id);
              const url = publicClientUrl(client.handle);
              const emailCount = client.emails?.length ?? (client.email ? 1 : 0);
              const inviteLine = inviteEmailSummary(client);
              return (
                <Card
                  key={client.id}
                  className="dream-float flex cursor-pointer flex-col"
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("a, button")) return;
                    openEdit(client);
                  }}
                >
                  <CardHeader>
                    <div className="flex min-w-0 items-center gap-2.5">
                      <ClientMark
                        color={client.color}
                        logoUrl={client.logoUrl}
                        name={client.name}
                        size={36}
                      />
                      <CardTitle className="truncate">{client.name}</CardTitle>
                    </div>
                  </CardHeader>
                  <div className="flex-1 px-4 py-3">
                    <p className="text-[0.75rem] text-[var(--color-ink-muted)]">
                      {clientProjects.length}{" "}
                      {clientProjects.length === 1 ? "projeto ou campanha" : "projetos e campanhas"}{" "}
                      · {emailCount} {emailCount === 1 ? "e-mail" : "e-mails"}
                    </p>
                    <div className="mt-3 flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface-muted)] px-2 py-1.5">
                      <code className="min-w-0 flex-1 truncate text-[0.75rem] text-[var(--color-ink)]">
                        /@{client.handle}
                      </code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 shrink-0 px-2"
                        onClick={() => void copyClientUrl(client.handle)}
                        aria-label={`Copiar ${url}`}
                      >
                        <IconCopy className="size-3.5" />
                      </Button>
                    </div>
                    <p className="mt-1.5 truncate text-[0.6875rem] text-[var(--color-ink-muted)]">
                      {inviteLine}
                    </p>
                    <p
                      className={[
                        "mt-1.5 text-[0.6875rem] font-medium",
                        client.hasPin
                          ? "text-[var(--dream-success-fg)]"
                          : "text-[var(--dream-warning-fg)]",
                      ].join(" ")}
                    >
                      {client.hasPin
                        ? "Senha de 4 dígitos definida"
                        : "Defina a senha para o cliente entrar"}
                    </p>
                    {clientProjects.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {clientProjects.map((project) => (
                          <li
                            key={project.id}
                            className="truncate text-[0.8125rem] text-[var(--color-ink-secondary)]"
                          >
                            {project.name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {client.notes !== null && client.notes !== "" && (
                      <p className="mt-2 line-clamp-3 text-[0.8125rem] leading-5 text-[var(--color-ink-secondary)]">
                        {client.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 border-t border-[var(--color-line)] px-4 py-2.5">
                    <Link
                      to="/calendario"
                      search={calendarSearch({ clients: client.id })}
                      className="inline-flex h-8 items-center rounded-lg bg-brand-50 px-2.5 text-[0.8125rem] font-semibold text-brand-700 transition-[background-color] duration-[var(--dream-duration-fast)] hover:bg-brand-100"
                    >
                      Calendário
                    </Link>
                    <button
                      type="button"
                      className="inline-flex h-8 items-center rounded-lg px-2.5 text-[0.8125rem] font-medium text-[var(--color-ink-secondary)] transition-[background-color,color] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]"
                      onClick={() => openEdit(client)}
                    >
                      Configurar
                    </button>
                    <a
                      className="ml-auto text-[0.8125rem] font-medium text-primary hover:underline"
                      href={url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Abrir portal
                    </a>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        open={editorOpen}
        onOpenChange={(open) => setEditorOpen(open)}
        title={editing === null ? "Novo cliente" : editing.name}
        description={
          editing === null
            ? "Cadastro, senha do portal, e-mails da equipe para o convite da Agenda e o que o cliente vê."
            : "Edite identidade, portal, e-mails da equipe e o que aparece na agenda pública."
        }
        size="lg"
      >
        <ClientEditorDialog
          open={editorOpen}
          client={editing}
          onClose={() => setEditorOpen(false)}
        />
      </Dialog>
    </div>
  );
}
